import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    OnInit,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    FormArray,
    FormControl,
    Validators,
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { InstructivoEmbarqueStateService, InstructivoEmbarqueData } from '../../../services/instructivo-embarque-state.service';
import { InstructivoEmbarqueService } from '../../../services/instructivo-embarque.service';
import { customerReferenceRequiredTrim } from './step3-panel-validators.util';
import { PartyDetailDto } from '@opb-workspace/shared';
import {
    ApplyBookingResponseDto,
    CreateShippingInstructionItemDto,
    CreateShippingInstructionSpeciesDto,
    TypeOfShipment,
    FreightCondition,
    AntecedentesCatalogsDto,
    ConditionCatalogItemDto,
    SpeciesCatalogItemDto,
    VarietyCatalogItemDto,
    ContainerPackageTypeDto,
    ColdTreatmentIndicatorDto,
    ControlledAtmosphereDto,
    VentilationUnitDto,
    SpeciesPackageTypeDto,
    PalletPackageTypeDto,
} from '@opb-workspace/shared';


/** Kilos por caja: si hay valor numérico, debe ser estrictamente mayor que cero. */
function greaterThanZeroKg(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const raw = control.value;
        if (raw === null || raw === undefined || raw === '') {
            return null;
        }
        const n =
            typeof raw === 'number' ? raw : Number(String(raw).trim().replace(',', '.'));
        if (!Number.isFinite(n) || n <= 0) {
            return { greaterThanZero: true };
        }
        return null;
    };
}

@Component({
    selector: 'app-step3-carga-financieros',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatTableModule,
        MatCheckboxModule,
        MatRadioModule,
        MatTooltipModule,
        MatExpansionModule,
        MatAutocompleteModule,
    ],
    templateUrl: './step3-carga-financieros.component.html',
    styleUrls: ['./step3-carga-financieros.component.scss'],
})
export class Step3CargaFinancierosComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    form!: FormGroup;

    // Enum references
    TypeOfShipment = TypeOfShipment;
    FreightCondition = FreightCondition;

    // Instructivos de embarque — cada panel con su propio FormGroup aislado
    instructivoForms: FormGroup[] = [];

    // Party search state per panel (consignee / notify)
    private readonly consigneeOptionsByPanel = new Map<number, PartyDetailDto[]>();
    private readonly notifyOptionsByPanel = new Map<number, PartyDetailDto[]>();

    getConsigneeOptionsFor(panelIdx: number): PartyDetailDto[] {
        return this.consigneeOptionsByPanel.get(panelIdx) ?? [];
    }

    getNotifyOptionsFor(panelIdx: number): PartyDetailDto[] {
        return this.notifyOptionsByPanel.get(panelIdx) ?? [];
    }

    searchConsigneeForPanel(panelIdx: number, query: string): void {
        this.service.searchParties('consignee', query ?? '').pipe(take(1)).subscribe({
            next: (rows) => { this.consigneeOptionsByPanel.set(panelIdx, rows); this.cdr.markForCheck(); },
            error: () => { this.consigneeOptionsByPanel.set(panelIdx, []); },
        });
    }

    searchNotifyForPanel(panelIdx: number, query: string): void {
        this.service.searchParties('notify', query ?? '').pipe(take(1)).subscribe({
            next: (rows) => { this.notifyOptionsByPanel.set(panelIdx, rows); this.cdr.markForCheck(); },
            error: () => { this.notifyOptionsByPanel.set(panelIdx, []); },
        });
    }

    onConsigneeSelected(panelIdx: number, party: PartyDetailDto): void {
        const fg = this.instructivoForms[panelIdx];
        if (!fg) return;
        fg.patchValue({
            consigneePartyId: party.partyId,
            consigneeName: (party.shortName || party.name || '').trim(),
            consigneeAddress: party.address ?? '',
        });
    }

    onNotifySelected(panelIdx: number, party: PartyDetailDto): void {
        const fg = this.instructivoForms[panelIdx];
        if (!fg) return;
        fg.patchValue({
            notifyPartyId: party.partyId,
            notifyName: (party.shortName || party.name || '').trim(),
            notifyAddress: party.address ?? '',
        });
    }

    private buildPanelForm(defaults?: Partial<InstructivoEmbarqueData>, preloadFromBooking = true): FormGroup {
        const bd = preloadFromBooking ? this.stateService.snapshot.bookingData : null;
        return this.fb.group({
            consigneePartyId: [defaults?.consigneePartyId ?? bd?.consignee?.partyId ?? null, Validators.required],
            consigneeName: [defaults?.consigneeName ?? bd?.consignee?.name ?? '', Validators.required],
            consigneeAddress: [defaults?.consigneeAddress ?? bd?.consignee?.address ?? ''],
            notifyPartyId: [defaults?.notifyPartyId ?? bd?.notify?.partyId ?? null, Validators.required],
            notifyName: [defaults?.notifyName ?? bd?.notify?.name ?? '', Validators.required],
            notifyAddress: [defaults?.notifyAddress ?? bd?.notify?.address ?? ''],
            // Ref. Cliente + Antecedentes Financieros: por instructivo desde feat-ie-wizard-mejoras
            // (puntos 2 y 3) — antes vivían en Step2/FormGroup global de Step3.
            customerReference: [
                defaults?.customerReference ?? bd?.customerReference ?? '',
                [customerReferenceRequiredTrim(), Validators.maxLength(200)],
            ],
            typeOfCargoId: [
                defaults?.typeOfCargoId ?? this.frigorizadosDefaultId() ?? null,
                Validators.required,
            ],
            methodOfPaymentId: [defaults?.methodOfPaymentId ?? null, Validators.required],
            modeOfSaleId: [defaults?.modeOfSaleId ?? null, Validators.required],
            clauseOfSaleId: [defaults?.clauseOfSaleId ?? null, Validators.required],
            freightCondition: [
                {
                    value: defaults?.freightCondition ?? bd?.freightCondition ?? null,
                    disabled: this.isFreightConditionLockedFromBooking(bd),
                },
                Validators.required,
            ],
            palletsChep: [defaults?.palletsChep ?? null],
            palletsChepQuantity: [defaults?.palletsChepQuantity ?? null],
            items: this.fb.array([]),
        });
    }

    /**
     * Condición de Flete queda bloqueada (solo lectura) cuando viene de un booking OPB con match
     * confiable del indicador legado (`vwBookings.ShipConvenienceIndicatorName` →
     * `wizard.service.ts::mapFreightFromShipConvenience()`). Si el indicador no matcheó ningún
     * valor del enum (`freightConditionBookingHint` sin `freightCondition`) o el booking es
     * externo (no OPB), el campo sigue editable — no hay nada confiable que bloquear.
     */
    private isFreightConditionLockedFromBooking(
        bd: ApplyBookingResponseDto | null,
    ): boolean {
        return bd?.isOpbBooking === true && bd?.freightCondition != null;
    }

    /**
     * El stepper puede montar Step 3 antes de que Step 2 termine de aplicar el booking OPB
     * (mismo patrón de carrera que `syncOpbBookingTipoEmbarqueLock()`): re-aplica el bloqueo de
     * Condición de Flete en todos los paneles ya creados cuando `bookingData` cambia.
     */
    private applyFreightConditionLockToAllPanels(): void {
        const bd = this.stateService.snapshot.bookingData;
        const locked = this.isFreightConditionLockedFromBooking(bd);
        this.instructivoForms.forEach((fg) => {
            const ctrl = fg.get('freightCondition');
            if (!ctrl) return;
            if (locked) {
                if (ctrl.value !== bd?.freightCondition) {
                    ctrl.setValue(bd?.freightCondition, { emitEvent: false });
                }
                ctrl.disable({ emitEvent: false });
            } else {
                ctrl.enable({ emitEvent: false });
            }
        });
    }

    /**
     * Busca por nombre (no hardcodea el Id, puede diferir entre ambientes/catálogos) el ítem
     * "FRIGORIZADOS" del catálogo de Tipo de Carga (feat-ie-wizard-mejoras, punto 6).
     */
    private frigorizadosDefaultId(): number | undefined {
        return this.catalogs.typeOfCargoes?.find(
            (c) => (c.name ?? '').trim().toUpperCase() === 'FRIGORIZADOS',
        )?.id;
    }

    /**
     * `loadCatalogs()` es async y puede resolver después de construir el panel #1 — si el
     * control de Tipo de Carga sigue vacío cuando el catálogo llega, aplica el default ahí.
     */
    private applyDefaultTypeOfCargoToEmptyPanels(): void {
        const frigId = this.frigorizadosDefaultId();
        if (frigId == null) return;
        this.instructivoForms.forEach((fg) => {
            const ctrl = fg.get('typeOfCargoId');
            if (ctrl && ctrl.value == null) {
                ctrl.setValue(frigId);
            }
        });
    }

    private subscribePanel(instrForm: FormGroup): void {
        instrForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.syncInstructivosToState();
        });
        // Pallets CHEP por panel (antes: control único del form global de Antecedentes Financieros).
        instrForm.get('palletsChep')?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((checked) => {
                if (!checked) {
                    instrForm.get('palletsChepQuantity')?.setValue(0);
                }
            });
    }

    private syncInstructivosToState(): void {
        const instructivos: InstructivoEmbarqueData[] = this.instructivoForms.map((instrForm) => {
            const v = instrForm.getRawValue() as {
                consigneePartyId?: number; consigneeName?: string; consigneeAddress?: string;
                notifyPartyId?: number; notifyName?: string; notifyAddress?: string;
                customerReference?: string;
                typeOfCargoId?: number; methodOfPaymentId?: number; modeOfSaleId?: number;
                clauseOfSaleId?: number; freightCondition?: FreightCondition;
                palletsChep?: boolean; palletsChepQuantity?: number;
                items?: CreateShippingInstructionItemDto[];
            };
            const rawItems = ((instrForm.get('items') as FormArray)?.getRawValue() ?? []) as CreateShippingInstructionItemDto[];
            return {
                consigneePartyId: v.consigneePartyId ?? undefined,
                consigneeName: v.consigneeName ?? undefined,
                consigneeAddress: v.consigneeAddress ?? undefined,
                notifyPartyId: v.notifyPartyId ?? undefined,
                notifyName: v.notifyName ?? undefined,
                notifyAddress: v.notifyAddress ?? undefined,
                customerReference: (v.customerReference ?? '').trim() || undefined,
                typeOfCargoId: v.typeOfCargoId ?? undefined,
                methodOfPaymentId: v.methodOfPaymentId ?? undefined,
                modeOfSaleId: v.modeOfSaleId ?? undefined,
                clauseOfSaleId: v.clauseOfSaleId ?? undefined,
                freightCondition: v.freightCondition ?? undefined,
                palletsChep: v.palletsChep ?? undefined,
                palletsChepQuantity:
                    v.palletsChepQuantity != null && Number(v.palletsChepQuantity) > 0
                        ? Number(v.palletsChepQuantity)
                        : undefined,
                items: this.enrichItemsForWizardState(rawItems),
            };
        });
        this.stateService.patchInstructivos(instructivos);
        const allItems = instructivos.flatMap((i) => i.items ?? []);
        this.stateService.patchFormData({ priceBox: this.computePriceBoxFromItems(allItems) });
    }

    addInstructivoPanel(): void {
        const defaultQty = this.remainingContainerCount > 0 ? this.remainingContainerCount : undefined;
        // Copiar del Instructivo #1: consignatario, notificante, ref. cliente y antecedentes
        // financieros (feat-ie-wizard-mejoras, punto 4 — decisión previa de crear el panel vacío
        // fue revertida por pedido del cliente). `buildPanelForm()` ignora `items` de `defaults`
        // (siempre parte con un FormArray vacío), así que pasar el raw value completo es seguro.
        const panel0Defaults = this.instructivoForms[0]?.getRawValue() as
            | Partial<InstructivoEmbarqueData>
            | undefined;
        const instrForm = this.buildPanelForm(panel0Defaults, false);
        this.instructivoForms.push(instrForm);
        this.subscribePanel(instrForm);
        const newPanelIdx = this.instructivoForms.length - 1;

        if (newPanelIdx > 0 && (this.getItemsFor(0)?.length ?? 0) > 0) {
            this.cloneItemsFromPanel0(newPanelIdx, defaultQty);
        } else {
            this.addItem(newPanelIdx, { quantityOfContainers: defaultQty });
        }
    }

    private cloneItemsFromPanel0(targetPanelIdx: number, defaultQty?: number): void {
        const sourceItems = this.getItemsFor(0);

        for (let i = 0; i < sourceItems.length; i++) {
            const srcItem = (sourceItems.at(i) as FormGroup).getRawValue();
            const srcSpeciesArr = sourceItems.at(i).get('species') as FormArray;

            const clonedSpecies: Partial<CreateShippingInstructionSpeciesDto>[] = [];
            for (let j = 0; j < srcSpeciesArr.length; j++) {
                const sp = (srcSpeciesArr.at(j) as FormGroup).getRawValue();
                clonedSpecies.push({
                    speciesId: sp.speciesId,
                    speciesName: sp.speciesName,
                    conditionId: sp.conditionId,
                    condition: sp.condition,
                    varietyId: sp.varietyId ?? null,
                    variety: sp.variety ?? '',
                    boxCount: sp.boxCount || 0,
                    typeOfPackageId: sp.typeOfPackageId ?? null,
                    packageType: sp.packageType ?? '',
                    // Copiar del Instructivo #1 (feat-ie-wizard-mejoras, punto 4 — antes se
                    // vaciaban a propósito, decisión revertida por pedido del cliente):
                    netWeightKgPerBox: sp.netWeightKgPerBox ?? undefined,
                    grossWeightKgPerBox: sp.grossWeightKgPerBox ?? undefined,
                    fobUnitPrice: sp.fobUnitPrice ?? undefined,
                    brand: sp.brand ?? '',
                });
            }

            this.addItem(targetPanelIdx, {
                itemNumber: i + 1,
                // Solo el primer ítem recibe la cantidad por defecto; los demás quedan para completar
                quantityOfContainers: i === 0 ? defaultQty : undefined,
                containerTypeId: srcItem.containerTypeId,
                containerType: srcItem.containerType,
                coldTreatmentIndicatorId: srcItem.coldTreatmentIndicatorId,
                coldTreatment: srcItem.coldTreatment,
                controlledAtmosphereId: srcItem.controlledAtmosphereId,
                atmosphere: srcItem.atmosphere,
                atmosphereParams: srcItem.atmosphereParams,
                temperatureCelsius: srcItem.temperatureCelsius,
                temperatureUnit: srcItem.temperatureUnit ?? 'C',
                ventilation: srcItem.ventilation,
                ventilationUnitId: srcItem.ventilationUnitId,
                ventilationUnit: srcItem.ventilationUnit,
                species: clonedSpecies as CreateShippingInstructionSpeciesDto[],
            });
        }

        if (this.isOpbBooking) {
            this.applyOpbItemFieldLocks();
        }

        this.hydrateVarietiesForPanel(targetPanelIdx);
    }

    private hydrateVarietiesForPanel(panelIdx: number): void {
        const items = this.getItemsFor(panelIdx);
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            const speciesArr = this.getSpecies(panelIdx, i);
            for (let j = 0; j < speciesArr.length; j++) {
                const g = speciesArr.at(j) as FormGroup;
                const sid = g.get('speciesId')?.value as number | null;
                if (sid == null || !Number.isFinite(Number(sid))) continue;
                const key = this.speciesKey(panelIdx, i, j);
                this.service.getVarietiesCatalog(Number(sid)).pipe(take(1)).subscribe({
                    next: (rows) => {
                        const vidRaw = g.get('varietyId')?.value;
                        const vtext = (g.get('variety')?.value ?? '').toString().trim();
                        const vid = vidRaw != null && vidRaw !== '' ? Number(vidRaw) : null;
                        let merged = rows;
                        if (vid != null && Number.isFinite(vid) && !rows.some((r) => r.varietyId === vid)) {
                            merged = [...rows, { varietyId: vid, merchandiseId: Number(sid), varietyName: vtext || `Variedad #${vid}` }];
                        }
                        this.varietiesBySpeciesKey.set(key, merged);
                        this.cdr.markForCheck();
                    },
                    error: () => { this.varietiesBySpeciesKey.set(key, []); },
                });
            }
        }
    }

    removeInstructivoPanel(index: number): void {
        this.instructivoForms.splice(index, 1);
    }

    getItemsFor(panelIdx: number): FormArray {
        return this.instructivoForms[panelIdx]?.get('items') as FormArray;
    }

    getSpeciesFor(panelIdx: number, itemIdx: number): FormArray {
        return this.getItemsFor(panelIdx)?.at(itemIdx)?.get('species') as FormArray;
    }

    get pendingContainerCount(): number | undefined {
        return this.stateService.snapshot.bookingData?.pendingContainerCount;
    }

    get totalContainerCount(): number {
        if (this.instructivoForms.length === 0) return 0;
        return this.instructivoForms.reduce((total, instrForm) => {
            const items = instrForm.get('items') as FormArray;
            if (!items) return total;
            return total + items.controls.reduce(
                (sum, ctrl) => sum + (Number(ctrl.get('quantityOfContainers')?.value) || 0),
                0,
            );
        }, 0);
    }

    totalContainerCountFor(panelIdx: number): number {
        const items = this.getItemsFor(panelIdx);
        if (!items) return 0;
        return items.controls.reduce(
            (sum, ctrl) => sum + (Number(ctrl.get('quantityOfContainers')?.value) || 0),
            0,
        );
    }

    get remainingContainerCount(): number {
        return Math.max(0, (this.pendingContainerCount ?? 0) - this.totalContainerCount);
    }

    clampContainerCount(panelIdx: number, itemIdx: number): void {
        if (!this.isOpbBooking || this.pendingContainerCount == null) return;
        const ctrl = this.getItemsFor(panelIdx)?.at(itemIdx)?.get('quantityOfContainers');
        if (!ctrl) return;
        const thisValue = Number(ctrl.value) || 0;
        const otherTotal = this.totalContainerCount - thisValue;
        const maxAllowed = Math.max(0, this.pendingContainerCount - otherTotal);
        if (thisValue > maxAllowed) {
            ctrl.setValue(maxAllowed);
        }
    }

    // Catalogs
    catalogs: AntecedentesCatalogsDto = {
        clauseOfSales: [],
        typeOfCargoes: [],
        methodOfPayments: [],
        modeOfSales: [],
        wayOfTransports: [],
        fobCurrencies: [],
        freightConditions: [],
    };

    /** Tipos de bulto contenedor (SP + API; mocks en dev). */
    containerPackageTypes: ContainerPackageTypeDto[] = [];
    /** Tratamiento frío: `tblParameterValues` ParameterId = 62 (legacy). */
    coldTreatmentIndicators: ColdTreatmentIndicatorDto[] = [];
    /** Atmósferas CA (`tblControlledAtmospheres`). */
    controlledAtmospheres: ControlledAtmosphereDto[] = [];
    /** Unidades de ventilación (`tblParameterValues` ParameterId = 22). */
    ventilationUnits: VentilationUnitDto[] = [];
    /** Tipos de embalaje por especie (`tblTypeOfPackages`). */
    speciesPackageTypes: SpeciesPackageTypeDto[] = [];
    /** Tipo bulto pallet — unidad primaria (`tblTypeOfPackagePrimaryTransportUnits`, SP pallet). */
    palletPackageTypes: PalletPackageTypeDto[] = [];
    temperatureUnits = ['C', 'F'];

    /** Con booking OPB el instructivo va siempre en contenedor (spec cliente). */
    isOpbBooking = false;
    isLoading = false;

    /**
     * Firma del último volcado desde estado (ítems + cantidad de especies por línea).
     * Evita duplicar filas y permite refrescar si el backend devuelve nuevas especies.
     */
    private lastSyncedCargoSignature = '';

    // Display helpers
    // Nota v1: ContainerNumber + Seal NO se ingresan en este wizard
    // (se asignan en módulo Couriers que es v2)
    displayColumns = ['itemNumber', 'containerType', 'actions'];
    displaySpeciesColumns = [
        'speciesName',
        'variety',
        'boxCount',
        'totalNetKg',
        'totalGrossKg',
        'fobItemTotal',
        'actions',
    ];
    speciesCatalog: SpeciesCatalogItemDto[] = [];
    conditionCatalog: ConditionCatalogItemDto[] = [];
    private readonly varietiesBySpeciesKey = new Map<
        string,
        VarietyCatalogItemDto[]
    >();

    private readonly cdr = inject(ChangeDetectorRef);

    /** mat-select con [value] numérico a veces compara mal string vs number (booking/API). */
    /** Alinea catálogo antecedentes (id 1–4) con `FreightCondition` del DTO. */
    freightEnumFromCatalogId(id: number): FreightCondition {
        const m: Record<number, FreightCondition> = {
            1: FreightCondition.Collect,
            2: FreightCondition.FullCollect,
            3: FreightCondition.Prepaid,
            4: FreightCondition.FullPrepaid,
        };
        return m[id] ?? FreightCondition.Collect;
    }

    compareNumericOption = (a: unknown, b: unknown): boolean => {
        if (a == null && b == null) {
            return true;
        }
        if (a == null || b == null) {
            return false;
        }
        const na = Number(a);
        const nb = Number(b);
        return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
    };

    constructor(
        private fb: FormBuilder,
        public stateService: InstructivoEmbarqueStateService,
        private service: InstructivoEmbarqueService
    ) {}

    ngOnInit(): void {
        const initialOpb =
            this.stateService.snapshot.bookingData?.isOpbBooking === true;
        this.isOpbBooking = initialOpb;
        this.initForm();
        this.loadSpeciesAndConditionsCatalogsThenResyncCargo();
        this.loadCatalogs();
        this.loadContainerPackageTypes();
        this.loadColdTreatmentIndicators();
        this.loadControlledAtmospheres();
        this.loadVentilationUnits();
        this.loadSpeciesPackageTypes();
        this.loadPalletPackageTypes();

        const existingFormData = this.stateService.snapshot.formData;
        if (existingFormData.typeOfShipmentId) {
            const opbType = initialOpb ? this.opbTypeOfShipment : undefined;
            const forced =
                initialOpb && existingFormData.typeOfShipmentId !== opbType;
            const typeId = forced ? opbType! : existingFormData.typeOfShipmentId;
            this.form.patchValue({
                typeOfShipmentId: typeId,
                quantityOfPallets:
                    forced && typeId === TypeOfShipment.Container
                        ? null
                        : existingFormData.quantityOfPallets,
                wayOfTransportId: existingFormData.wayOfTransportId,
            });
        }
        // Nota: Ref. Cliente y Antecedentes Financieros (clauseOfSaleId, typeOfCargoId,
        // methodOfPaymentId, modeOfSaleId, freightCondition, palletsChep, palletsChepQuantity)
        // ya no viven en `this.form` (header) — son por-panel desde feat-ie-wizard-mejoras
        // (puntos 2 y 3); `buildPanelForm()` los precarga por instructivo.

        if (initialOpb) {
            // Ítems OPB: los vuelca `trySyncCargoItemsFromWizardState` desde el estado
            // (el componente se monta antes de aplicar el booking).
        } else {
            // Booking externo: inicializar con un panel por defecto
            const existingPanel0 = this.stateService.snapshot.instructivos[0];
            const defaultPanel = this.buildPanelForm(existingPanel0 ?? {});
            this.instructivoForms.push(defaultPanel);
            this.subscribePanel(defaultPanel);
            if (existingFormData.items && existingFormData.items.length > 0) {
                existingFormData.items.forEach((item) =>
                    this.addItemFromData(0, item)
                );
            } else if (
                existingFormData.typeOfShipmentId === TypeOfShipment.Pallet &&
                existingFormData.quantityOfPallets != null &&
                existingFormData.quantityOfPallets > 0
            ) {
                this.addItemFromData(0, {
                    itemNumber: 1,
                    quantityOfPallets: existingFormData.quantityOfPallets,
                    species: [],
                });
            } else if (existingFormData.items == null) {
                // Primera visita sin ítems previos → sembrar fila vacía
                this.addItemFromData(0, { itemNumber: 1, species: [] });
                this.addSpecies(0, 0);
            }
        }
        this.applyAllItemValidators();
        this.form.updateValueAndValidity({ emitEvent: false });

        this.form.get('typeOfShipmentId')?.valueChanges.subscribe((val) => {
            const forcedType = this.resolveForcedOpbType(val);
            if (forcedType != null) {
                this.typeOfShipmentControl.setValue(forcedType, {
                    emitEvent: false,
                });
                this.onTypeOfShipmentChange(forcedType);
                this.cdr.markForCheck();
                return;
            }
            this.onTypeOfShipmentChange(val);
        });

        this.form.valueChanges.subscribe(() => {
            const val = this.form.getRawValue();
            const allRawItems = this.instructivoForms.flatMap(
                (f) => ((f.get('items') as FormArray)?.getRawValue() ?? []) as CreateShippingInstructionItemDto[]
            );
            let headerPallets: number | undefined;
            if (val.typeOfShipmentId === TypeOfShipment.Pallet && allRawItems.length) {
                const sum = allRawItems.reduce(
                    (acc: number, it: CreateShippingInstructionItemDto) => acc + (Number(it?.quantityOfPallets) || 0), 0,
                );
                if (sum > 0) headerPallets = sum;
            }
            this.stateService.patchFormData({
                typeOfShipmentId: val.typeOfShipmentId,
                quantityOfPallets: val.typeOfShipmentId === TypeOfShipment.Pallet ? headerPallets : undefined,
                wayOfTransportId: val.wayOfTransportId ?? undefined,
                valorFobCurrencyId: undefined,
                customsId: undefined,
                customsPresentationName: undefined,
                daysOfPaymentIvv: undefined,
                brand: undefined,
                customsRemarks: undefined,
            });
            this.syncInstructivosToState();
        });

        this.stateService.state
            .pipe(
                map((s) => s.bookingData?.isOpbBooking === true),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((isOpb) => {
                this.isOpbBooking = isOpb;
                this.syncOpbBookingTipoEmbarqueLock(isOpb);
            });

        // Solo re-sincronizar los paneles cuando cambia el booking o su tipo (OPB/externo).
        // NO re-sincronizar por cambios en itemCount/speciesTotal: esos cambios los produce
        // form.valueChanges → patchFormData y provocarían que todos los paneles se aplasten
        // en uno solo cada vez que el usuario edita un campo del formulario principal.
        this.stateService.state
            .pipe(
                map((s) => ({
                    bid: s.formData.bookingId,
                    isOpb: s.bookingData?.isOpbBooking === true,
                })),
                distinctUntilChanged(
                    (a, b) => a.bid === b.bid && a.isOpb === b.isOpb,
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.trySyncCargoItemsFromWizardState());

        queueMicrotask(() => {
            this.syncOpbBookingTipoEmbarqueLock(
                this.stateService.snapshot.bookingData?.isOpbBooking === true,
            );
            this.trySyncCargoItemsFromWizardState();
        });
    }

    /**
     * Los mat-select de especie/condición necesitan `mat-option` cargadas antes de mostrar
     * valores precargados (booking OPB). Antes solo se pedía catálogo al hacer foco → UI vacía.
     */
    private loadSpeciesAndConditionsCatalogsThenResyncCargo(): void {
        forkJoin({
            species: this.service.getSpeciesCatalog(),
            conditions: this.service.getConditionsCatalog(),
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ species, conditions }) => {
                    this.speciesCatalog = species;
                    this.conditionCatalog = conditions;
                    this.mergeCatalogExtrasFromBookingItems(
                        this.stateService.snapshot.formData.items,
                    );
                    this.lastSyncedCargoSignature = '';
                    this.trySyncCargoItemsFromWizardState();
                    this.cdr.detectChanges();
                },
                error: () => {
                    console.error(
                        'Error cargando catálogos de especies / condiciones',
                    );
                },
            });
    }

    /**
     * Asegura que IDs traídos por apply-booking existan como opción (p. ej. nombre no coincide con catálogo masivo).
     */
    /** IDs de tipo embalaje del booking que aún no están en el catálogo cargado. */
    private mergeTypeOfPackageExtrasFromSnapshot(): void {
        const items = this.stateService.snapshot.formData.items;
        if (!items?.length) {
            return;
        }
        const seen = new Set(
            this.speciesPackageTypes.map((x) => x.typeOfPackageId),
        );
        for (const it of items) {
            for (const sp of it.species ?? []) {
                const tid = sp.typeOfPackageId;
                if (
                    tid == null ||
                    !Number.isFinite(Number(tid)) ||
                    seen.has(Number(tid))
                ) {
                    continue;
                }
                seen.add(Number(tid));
                const code =
                    (sp.packageType ?? '').trim() || `Emb. #${tid}`;
                this.speciesPackageTypes = [
                    ...this.speciesPackageTypes,
                    { typeOfPackageId: Number(tid), code },
                ];
            }
        }
    }

    private mergeCatalogExtrasFromBookingItems(
        items: CreateShippingInstructionItemDto[] | undefined,
    ): void {
        if (!items?.length) {
            return;
        }
        const seenS = new Set(this.speciesCatalog.map((x) => x.speciesId));
        const seenC = new Set(this.conditionCatalog.map((x) => x.conditionId));
        for (const it of items) {
            for (const sp of it.species ?? []) {
                const sid = sp.speciesId;
                if (
                    sid != null &&
                    Number.isFinite(Number(sid)) &&
                    !seenS.has(Number(sid))
                ) {
                    seenS.add(Number(sid));
                    const label =
                        (sp.speciesName ?? '').trim() || `Especie #${sid}`;
                    this.speciesCatalog = [
                        ...this.speciesCatalog,
                        { speciesId: Number(sid), speciesName: label },
                    ];
                }
                const cid = sp.conditionId;
                if (
                    cid != null &&
                    Number.isFinite(Number(cid)) &&
                    !seenC.has(Number(cid))
                ) {
                    seenC.add(Number(cid));
                    const clabel =
                        (sp.condition ?? '').trim() || `Condición #${cid}`;
                    this.conditionCatalog = [
                        ...this.conditionCatalog,
                        {
                            conditionId: Number(cid),
                            conditionName: clabel,
                        },
                    ];
                }
            }
        }
    }

    /**
     * Tipo de Embarque real del booking OPB (`ApplyBookingResponseDto.typeOfShipmentId`).
     * Fallback a Contenedor si el dato no vino (booking legacy sin `TypeOfShipmentId` seteado) —
     * mismo comportamiento por defecto que existía antes de este campo (feat-ie-wizard-mejoras,
     * punto 1). Solo tiene sentido para bookings OPB: para bookings externos el usuario elige.
     */
    private get opbTypeOfShipment(): TypeOfShipment {
        return (
            this.stateService.snapshot.bookingData?.typeOfShipmentId ??
            TypeOfShipment.Container
        );
    }

    /**
     * Con FormControl en `mat-radio-group`, `[disabled]` en el hijo no bloquea bien.
     * Refuerzo: si el valor del form no coincide con el tipo real del booking OPB, devuelve el
     * tipo real para forzarlo de vuelta (antes forzaba siempre Contenedor).
     */
    private resolveForcedOpbType(val: unknown): TypeOfShipment | undefined {
        if (
            this.stateService.snapshot.bookingData?.isOpbBooking !== true
        ) {
            return undefined;
        }
        const opbType = this.opbTypeOfShipment;
        const n = Number(val);
        return Number.isFinite(n) && n === opbType ? undefined : opbType;
    }

    /**
     * El stepper crea este componente antes de que en step 2 se aplique booking OPB;
     * hay que reaccionar cuando bookingData pasa a isOpbBooking o vuelve a externo (null).
     */
    private syncOpbBookingTipoEmbarqueLock(isOpb: boolean): void {
        const ctl = this.typeOfShipmentControl;
        if (!ctl) {
            return;
        }

        if (isOpb) {
            const opbType = this.opbTypeOfShipment;
            if (ctl.value !== opbType) {
                ctl.setValue(opbType, { emitEvent: false });
                this.onTypeOfShipmentChange(opbType);
            }
            this.stateService.patchFormData({
                typeOfShipmentId: opbType,
            });
            this.trySyncCargoItemsFromWizardState();
            this.applyOpbItemFieldLocks();
        } else {
            this.lastSyncedCargoSignature = '';
        }
    }

    /**
     * El paso 3 se monta antes de aplicar el booking OPB: los ítems llegan al estado
     * después y hay que volcar el FormArray desde `formData.items`.
     */
    private trySyncCargoItemsFromWizardState(): void {
        const snap = this.stateService.snapshot;
        if (snap.bookingData?.isOpbBooking !== true) {
            this.lastSyncedCargoSignature = '';
            return;
        }
        const bid = snap.formData.bookingId;
        const stateItems = snap.formData.items;
        if (bid == null || !stateItems?.length) {
            return;
        }

        const sig = this.cargoPreloadSignature(bid, stateItems);
        const firstPanelItems = this.instructivoForms.length > 0 ? this.getItemsFor(0) : null;
        if (sig === this.lastSyncedCargoSignature && firstPanelItems && firstPanelItems.length > 0) {
            return;
        }

        // Reset: un único panel para la precarga OPB
        this.instructivoForms.splice(0);
        const existingPanel0 = this.stateService.snapshot.instructivos[0];
        const instrForm = this.buildPanelForm(existingPanel0 ?? {});
        this.instructivoForms.push(instrForm);
        this.subscribePanel(instrForm);
        stateItems.forEach((row, idx) => {
            this.addItemFromData(0, {
                ...row,
                itemNumber: row.itemNumber ?? idx + 1,
            });
        });
        this.lastSyncedCargoSignature = sig;
        this.applyAllItemValidators();
        this.form.updateValueAndValidity({ emitEvent: false });
        this.hydrateVarietiesCatalogsAfterCargoPreload();
        this.applyOpbItemFieldLocks();
    }

    private applyOpbItemFieldLocks(): void {
        if (!this.isOpbBooking) return;

        // Condición de Flete: control a nivel-panel (no ítem), se resuelve aparte.
        this.applyFreightConditionLockToAllPanels();

        const ITEM_LOCK = [
            'containerTypeId', 'containerType',
            'cargoType', 'coldTreatmentIndicatorId', 'coldTreatment',
            'controlledAtmosphereId', 'atmosphere', 'atmosphereParams',
            'temperatureCelsius', 'temperatureUnit',
            'ventilation', 'ventilationUnitId', 'ventilationUnit',
            'humidity', 'quantityOfPallets', 'palletTypeId', 'palletType',
            'itemRemarks',
        ];
        const SPECIES_LOCK = ['speciesId', 'speciesName', 'conditionId', 'condition'];

        this.instructivoForms.forEach((_, panelIdx) => {
            const items = this.getItemsFor(panelIdx);
            for (let i = 0; i < items.length; i++) {
                const g = items.at(i) as FormGroup;
                for (const f of ITEM_LOCK) {
                    g.get(f)?.disable({ emitEvent: false });
                }
                // `quantityOfContainers` SIEMPRE editable (feedback cliente 2026-08, revierte el
                // bloqueo agregado antes por error): la disponibilidad de contenedores del booking
                // se rastrea por SUMA de cantidades entre todos los instructivos activos, no por
                // identidad de contenedor específico — `clampContainerCount()` ya limita que esa
                // suma no supere `pendingContainerCount`. Ver lección #102 (modelo SUM) y #103 en
                // docs/claude/lessons/04-modulos.md.
                const sp = this.getSpecies(panelIdx, i);
                for (let j = 0; j < sp.length; j++) {
                    const sg = sp.at(j) as FormGroup;
                    for (const f of SPECIES_LOCK) {
                        sg.get(f)?.disable({ emitEvent: false });
                    }
                }
            }
        });
    }

    private cargoPreloadSignature(
        bid: number,
        items: CreateShippingInstructionItemDto[],
    ): string {
        return JSON.stringify({
            bid,
            rows: items.map((it) => ({
                itemNumber: it.itemNumber,
                species: (it.species ?? []).map((s) => ({
                    speciesId: s.speciesId,
                    boxCount: s.boxCount,
                    conditionId: s.conditionId,
                    typeOfPackageId: s.typeOfPackageId,
                })),
            })),
        });
    }

    /** Tras precarga OPB, pide variedades para combos que ya tienen `speciesId`. */
    private hydrateVarietiesCatalogsAfterCargoPreload(): void {
        this.instructivoForms.forEach((_, panelIdx) => {
            const items = this.getItemsFor(panelIdx);
            for (let i = 0; i < items.length; i++) {
                const speciesArr = this.getSpecies(panelIdx, i);
                for (let j = 0; j < speciesArr.length; j++) {
                    const g = speciesArr.at(j) as FormGroup;
                    const sid = g.get('speciesId')?.value as number | null;
                    if (sid == null || !Number.isFinite(Number(sid))) {
                        continue;
                    }
                    const key = this.speciesKey(panelIdx, i, j);
                    this.service.getVarietiesCatalog(Number(sid)).pipe(take(1)).subscribe({
                        next: (rows) => {
                            const vidRaw = g.get('varietyId')?.value;
                            const vtext = (g.get('variety')?.value ?? '')
                                .toString()
                                .trim();
                            const vid =
                                vidRaw != null && vidRaw !== ''
                                    ? Number(vidRaw)
                                    : null;
                            let merged = rows;
                            if (
                                vid != null &&
                                Number.isFinite(vid) &&
                                !rows.some((r) => r.varietyId === vid)
                            ) {
                                merged = [
                                    ...rows,
                                    {
                                        varietyId: vid,
                                        merchandiseId: Number(sid),
                                        varietyName: vtext || `Variedad #${vid}`,
                                    },
                                ];
                            }
                            this.varietiesBySpeciesKey.set(key, merged);
                            this.cdr.markForCheck();
                        },
                        error: () => { this.varietiesBySpeciesKey.set(key, []); },
                    });
                }
            }
        });
    }

    private initForm(): void {
        this.form = this.fb.group(
            {
                // Tipo Embarque (HEADER)
                typeOfShipmentId: [TypeOfShipment.Container, Validators.required],
                quantityOfPallets: [null],

                // Items (solo si Contenedor; empieza vacío)
                items: this.fb.array([]),

                // Vía transporte: HEADER único (no se movió — el resto de Antecedentes
                // Financieros pasó a ser por-panel en `buildPanelForm()`, feat-ie-wizard-mejoras
                // puntos 2 y 3).
                wayOfTransportId: [null],
            },
            {
                validators: [
                    (group: AbstractControl): ValidationErrors | null => {
                        const fg = group as FormGroup;
                        const raw = fg.getRawValue() as { typeOfShipmentId?: TypeOfShipment };
                        const typeId = raw.typeOfShipmentId;
                        if (typeId !== TypeOfShipment.Container && typeId !== TypeOfShipment.Pallet) {
                            return null;
                        }
                        const total = this.instructivoForms.reduce(
                            (sum, f) => sum + ((f.get('items') as FormArray)?.length ?? 0),
                            0,
                        );
                        return total < 1 ? { cargoItemsRequired: true } : null;
                    },
                ],
            },
        );
    }

    private loadCatalogs(): void {
        this.service.getCatalogs().subscribe({
            next: (catalogs) => {
                this.catalogs = catalogs;
                this.applyDefaultTypeOfCargoToEmptyPanels();
            },
            error: () => {
                console.error('Error cargando catálogos');
            },
        });
    }

    private loadContainerPackageTypes(): void {
        this.service.getContainerPackageTypes().subscribe({
            next: (rows) => {
                this.containerPackageTypes = rows;
                this.syncLegacyCatalogIdsIntoItems();
                this.patchItemsSnapshotFromForm();
            },
            error: () => {
                console.error('Error cargando tipos de bulto contenedor');
                this.containerPackageTypes = [];
            },
        });
    }

    private loadColdTreatmentIndicators(): void {
        this.service.getColdTreatmentIndicators().subscribe({
            next: (rows) => {
                this.coldTreatmentIndicators = rows;
                this.syncLegacyCatalogIdsIntoItems();
                this.patchItemsSnapshotFromForm();
            },
            error: () => {
                console.error('Error cargando indicadores de tratamiento frío');
                this.coldTreatmentIndicators = [];
            },
        });
    }

    private loadControlledAtmospheres(): void {
        this.service.getControlledAtmospheres().subscribe({
            next: (rows) => {
                this.controlledAtmospheres = rows;
                this.syncLegacyCatalogIdsIntoItems();
                this.patchItemsSnapshotFromForm();
            },
            error: () => {
                console.error('Error cargando atmósferas controladas');
                this.controlledAtmospheres = [];
            },
        });
    }

    private loadVentilationUnits(): void {
        this.service.getVentilationUnits().subscribe({
            next: (rows) => {
                this.ventilationUnits = rows;
                this.syncLegacyCatalogIdsIntoItems();
                this.patchItemsSnapshotFromForm();
            },
            error: () => {
                console.error('Error cargando unidades de ventilación');
                this.ventilationUnits = [];
            },
        });
    }

    private loadSpeciesPackageTypes(): void {
        this.service.getSpeciesPackageTypes().subscribe({
            next: (rows) => {
                this.speciesPackageTypes = rows;
                this.mergeTypeOfPackageExtrasFromSnapshot();
                this.syncLegacyCatalogIdsIntoItems();
                this.patchItemsSnapshotFromForm();
                if (
                    this.stateService.snapshot.bookingData?.isOpbBooking ===
                    true
                ) {
                    this.cdr.detectChanges();
                }
            },
            error: () => {
                console.error('Error cargando tipos de embalaje (especies)');
                this.speciesPackageTypes = [];
            },
        });
    }

    private loadPalletPackageTypes(): void {
        this.service.getPalletPackageTypes().subscribe({
            next: (rows) => {
                this.palletPackageTypes = rows;
                this.syncLegacyCatalogIdsIntoItems();
                this.patchItemsSnapshotFromForm();
            },
            error: () => {
                console.error('Error cargando tipos de bulto pallet');
                this.palletPackageTypes = [];
            },
        });
    }

    private syncLegacyCatalogIdsIntoItems(): void {
        this.syncContainerTypeIdsFromCodes();
        this.syncPalletTypeIdsFromCodes();
        this.syncColdTreatmentIdsFromLabels();
        this.syncControlledAtmosphereIdsFromNames();
        this.syncVentilationUnitIdsFromCodes();
        this.syncSpeciesPackageTypeIdsFromCodes();
    }

    private patchItemsSnapshotFromForm(): void {
        if (!this.form) return;
        const allItems: CreateShippingInstructionItemDto[] = [];
        this.instructivoForms.forEach((instrForm) => {
            const items = instrForm.get('items') as FormArray;
            if (items) {
                allItems.push(...(items.getRawValue() as CreateShippingInstructionItemDto[]));
            }
        });
        const itemsForState = this.enrichItemsForWizardState(allItems);
        this.stateService.patchFormData({
            items: itemsForState,
            priceBox: this.computePriceBoxFromItems(itemsForState),
        });
    }

    private computePriceBoxFromItems(
        items: CreateShippingInstructionItemDto[],
    ): string | undefined {
        for (const item of items) {
            for (const sp of item.species ?? []) {
                const price = Number(sp.fobUnitPrice);
                if (Number.isFinite(price) && price > 0) {
                    return String(price);
                }
            }
        }
        return undefined;
    }

    /**
     * Resuelve `containerTypeId` cuando solo viene código (p. ej. booking).
     */
    private syncContainerTypeIdsFromCodes(): void {
        if (!this.form) return;
        this.instructivoForms.forEach((_, panelIdx) => {
            const items = this.getItemsFor(panelIdx);
            if (!items?.length) return;
            for (let i = 0; i < items.length; i++) {
                const g = items.at(i) as FormGroup;
                const idCtrl = g.get('containerTypeId');
                const codeCtrl = g.get('containerType');
                if (idCtrl?.value != null && idCtrl.value !== '') continue;
                const code = (codeCtrl?.value ?? '').toString().trim().toUpperCase();
                if (!code) continue;
                const row = this.containerPackageTypes.find(
                    (x) => x.code.trim().toUpperCase() === code,
                );
                if (row) idCtrl?.setValue(row.containerTypeId, { emitEvent: false });
            }
        });
    }

    private syncPalletTypeIdsFromCodes(): void {
        if (!this.form || !this.palletPackageTypes.length) return;
        this.instructivoForms.forEach((_, panelIdx) => {
            const items = this.getItemsFor(panelIdx);
            if (!items?.length) return;
            for (let i = 0; i < items.length; i++) {
                const g = items.at(i) as FormGroup;
                const idCtrl = g.get('palletTypeId');
                const codeCtrl = g.get('palletType');
                if (idCtrl?.value != null && idCtrl.value !== '') continue;
                const code = (codeCtrl?.value ?? '').toString().trim().toUpperCase();
                if (!code) continue;
                const row = this.palletPackageTypes.find(
                    (x) => x.code.trim().toUpperCase() === code,
                );
                if (row) idCtrl?.setValue(row.palletTypeId, { emitEvent: false });
            }
        });
    }

    private enrichItemsForWizardState(
        items: CreateShippingInstructionItemDto[],
    ): CreateShippingInstructionItemDto[] {
        return this.enrichItemsSpeciesTotals(
            this.enrichItemsSpeciesPackageTypes(
                this.enrichItemsWithVentilationCodes(
                    this.enrichItemsWithControlledAtmosphereNames(
                        this.enrichItemsWithColdTreatmentLabels(
                            this.enrichItemsWithPalletCodes(
                                this.enrichItemsWithContainerCodes(items),
                            ),
                        ),
                    ),
                ),
            ),
        );
    }

    private enrichItemsSpeciesTotals(
        items: CreateShippingInstructionItemDto[],
    ): CreateShippingInstructionItemDto[] {
        return items.map((it) => ({
            ...it,
            species: (it.species ?? []).map((sp) => {
                const boxes = Number(sp.boxCount) || 0;
                const net = Number(sp.netWeightKgPerBox) || 0;
                const gross = Number(sp.grossWeightKgPerBox) || 0;
                const fobUnit = Number(sp.fobUnitPrice) || 0;
                return {
                    ...sp,
                    totalNetWeightKg: boxes * net || undefined,
                    totalGrossWeightKg: boxes * gross || undefined,
                    fobItemTotal: boxes * fobUnit || undefined,
                };
            }),
        }));
    }

    private enrichItemsWithPalletCodes(
        items: CreateShippingInstructionItemDto[],
    ): CreateShippingInstructionItemDto[] {
        if (!items.length || !this.palletPackageTypes.length) {
            return items;
        }
        return items.map((it) => {
            const id = it.palletTypeId;
            if (id == null) {
                return it;
            }
            const row = this.palletPackageTypes.find(
                (x) => x.palletTypeId === id,
            );
            if (!row) {
                return it;
            }
            return { ...it, palletType: row.code };
        });
    }

    private enrichItemsWithContainerCodes(
        items: CreateShippingInstructionItemDto[],
    ): CreateShippingInstructionItemDto[] {
        if (!items.length || !this.containerPackageTypes.length) {
            return items;
        }
        return items.map((it) => {
            const id = it.containerTypeId;
            if (id == null) {
                return it;
            }
            const row = this.containerPackageTypes.find(
                (x) => x.containerTypeId === id,
            );
            if (!row) {
                return it;
            }
            return { ...it, containerType: row.code };
        });
    }

    /**
     * Rellena `coldTreatment` (texto) desde el id para el DTO / PDF.
     */
    private enrichItemsWithColdTreatmentLabels(
        items: CreateShippingInstructionItemDto[],
    ): CreateShippingInstructionItemDto[] {
        if (!items.length || !this.coldTreatmentIndicators.length) {
            return items;
        }
        return items.map((it) => {
            const id = it.coldTreatmentIndicatorId;
            if (id == null) {
                return it;
            }
            const row = this.coldTreatmentIndicators.find(
                (x) => x.coldTreatmentIndicatorId === id,
            );
            if (!row) {
                return it;
            }
            return { ...it, coldTreatment: row.name };
        });
    }

    /**
     * Resuelve id cuando solo viene texto (p. ej. booking o estado previo).
     */
    private syncColdTreatmentIdsFromLabels(): void {
        if (!this.form || !this.coldTreatmentIndicators.length) return;
        this.instructivoForms.forEach((_, panelIdx) => {
            const items = this.getItemsFor(panelIdx);
            if (!items?.length) return;
            for (let i = 0; i < items.length; i++) {
                const g = items.at(i) as FormGroup;
                const idCtrl = g.get('coldTreatmentIndicatorId');
                const textCtrl = g.get('coldTreatment');
                if (idCtrl?.value != null && idCtrl.value !== '') continue;
                const text = (textCtrl?.value ?? '').toString().trim().toLowerCase();
                if (!text) continue;
                const row = this.coldTreatmentIndicators.find(
                    (x) => x.name.trim().toLowerCase() === text,
                );
                if (row) idCtrl?.setValue(row.coldTreatmentIndicatorId, { emitEvent: false });
            }
        });
    }

    private enrichItemsWithControlledAtmosphereNames(
        items: CreateShippingInstructionItemDto[],
    ): CreateShippingInstructionItemDto[] {
        if (!items.length || !this.controlledAtmospheres.length) {
            return items;
        }
        return items.map((it) => {
            const id = it.controlledAtmosphereId;
            if (id == null) {
                return it;
            }
            const row = this.controlledAtmospheres.find(
                (x) => x.controlledAtmosphereId === id,
            );
            if (!row) {
                return it;
            }
            return { ...it, atmosphere: row.name };
        });
    }

    private syncControlledAtmosphereIdsFromNames(): void {
        if (!this.form || !this.controlledAtmospheres.length) return;
        this.instructivoForms.forEach((_, panelIdx) => {
            const items = this.getItemsFor(panelIdx);
            if (!items?.length) return;
            for (let i = 0; i < items.length; i++) {
                const g = items.at(i) as FormGroup;
                const idCtrl = g.get('controlledAtmosphereId');
                const textCtrl = g.get('atmosphere');
                if (idCtrl?.value != null && idCtrl.value !== '') continue;
                const text = (textCtrl?.value ?? '').toString().trim().toLowerCase();
                if (!text) continue;
                const row = this.controlledAtmospheres.find(
                    (x) => x.name.trim().toLowerCase() === text,
                );
                if (row) idCtrl?.setValue(row.controlledAtmosphereId, { emitEvent: false });
            }
        });
    }

    private enrichItemsWithVentilationCodes(
        items: CreateShippingInstructionItemDto[],
    ): CreateShippingInstructionItemDto[] {
        if (!items.length || !this.ventilationUnits.length) {
            return items;
        }
        return items.map((it) => {
            const id = it.ventilationUnitId;
            if (id == null) {
                return it;
            }
            const row = this.ventilationUnits.find(
                (x) => x.ventilationUnitId === id,
            );
            if (!row) {
                return it;
            }
            return { ...it, ventilationUnit: row.code };
        });
    }

    private syncVentilationUnitIdsFromCodes(): void {
        if (!this.form || !this.ventilationUnits.length) return;
        this.instructivoForms.forEach((_, panelIdx) => {
            const items = this.getItemsFor(panelIdx);
            if (!items?.length) return;
            for (let i = 0; i < items.length; i++) {
                const g = items.at(i) as FormGroup;
                const idCtrl = g.get('ventilationUnitId');
                const codeCtrl = g.get('ventilationUnit');
                if (idCtrl?.value != null && idCtrl.value !== '') continue;
                const raw = (codeCtrl?.value ?? '').toString().trim();
                if (!raw) continue;
                const row = this.ventilationUnits.find((x) => {
                    const c = x.code.trim();
                    if (c === '%') return raw === '%';
                    return c.toUpperCase() === raw.toUpperCase();
                });
                if (row) idCtrl?.setValue(row.ventilationUnitId, { emitEvent: false });
            }
        });
    }

    private enrichItemsSpeciesPackageTypes(
        items: CreateShippingInstructionItemDto[],
    ): CreateShippingInstructionItemDto[] {
        if (!items.length || !this.speciesPackageTypes.length) {
            return items;
        }
        return items.map((it) => ({
            ...it,
            species: (it.species ?? []).map((sp) => {
                const id = sp.typeOfPackageId;
                if (id == null) {
                    return sp;
                }
                const row = this.speciesPackageTypes.find(
                    (x) => x.typeOfPackageId === id,
                );
                if (!row) {
                    return sp;
                }
                return { ...sp, packageType: row.code };
            }),
        }));
    }

    private syncSpeciesPackageTypeIdsFromCodes(): void {
        if (!this.form || !this.speciesPackageTypes.length) return;
        const validIds = new Set(
            this.speciesPackageTypes.map((x) => x.typeOfPackageId),
        );
        this.instructivoForms.forEach((_, panelIdx) => {
            const items = this.getItemsFor(panelIdx);
            if (!items?.length) return;
            for (let i = 0; i < items.length; i++) {
            const speciesArr = this.getSpecies(panelIdx, i);
            for (let j = 0; j < speciesArr.length; j++) {
                const g = speciesArr.at(j) as FormGroup;
                const idCtrl = g.get('typeOfPackageId');
                const codeCtrl = g.get('packageType');
                const rawId = idCtrl?.value;
                if (
                    rawId != null &&
                    rawId !== '' &&
                    !validIds.has(Number(rawId))
                ) {
                    idCtrl?.setValue(null, { emitEvent: false });
                }
                if (idCtrl?.value != null && idCtrl.value !== '') {
                    continue;
                }
                const raw = (codeCtrl?.value ?? '').toString().trim();
                if (!raw) {
                    continue;
                }
                const row = this.speciesPackageTypes.find(
                    (x) => x.code.toUpperCase() === raw.toUpperCase(),
                );
                if (row) {
                    idCtrl?.setValue(row.typeOfPackageId, { emitEvent: false });
                }
            }
            }
        });
    }

    private speciesKey(panelIdx: number, itemIndex: number, speciesIndex: number): string {
        return `${panelIdx}-${itemIndex}-${speciesIndex}`;
    }

    private preloadSpeciesCatalogIfNeeded(): void {
        if (this.speciesCatalog.length > 0) {
            return;
        }
        this.service.getSpeciesCatalog().subscribe({
            next: (rows) => {
                this.speciesCatalog = rows;
            },
            error: () => {
                console.error('Error cargando catálogo de especies');
            },
        });
    }

    private preloadConditionCatalogIfNeeded(): void {
        if (this.conditionCatalog.length > 0) {
            return;
        }
        this.service.getConditionsCatalog().subscribe({
            next: (rows) => {
                this.conditionCatalog = rows;
            },
            error: () => {
                console.error('Error cargando catálogo de condiciones');
            },
        });
    }

    private onTypeOfShipmentChange(_typeId: TypeOfShipment): void {
        const quantityControl = this.form.get('quantityOfPallets');

        // Limpiar ítems de todos los paneles
        this.instructivoForms.forEach((instrForm) => {
            const itemsControl = instrForm.get('items') as FormArray;
            while (itemsControl.length > 0) {
                itemsControl.removeAt(0);
            }
        });
        quantityControl?.clearValidators();
        quantityControl?.setValue(null, { emitEvent: false });
        quantityControl?.updateValueAndValidity({ emitEvent: false });
        this.applyAllItemValidators();
        this.form.updateValueAndValidity({ emitEvent: false });
    }

    private applyItemValidatorsFor(panelIdx: number): void {
        const items = this.getItemsFor(panelIdx);
        if (!items) return;
        const isPallet = this.typeOfShipment === TypeOfShipment.Pallet;
        for (let idx = 0; idx < items.length; idx++) {
            const g = items.at(idx) as FormGroup;
            const qty = g.get('quantityOfPallets');
            const ct = g.get('containerType');
            const ctId = g.get('containerTypeId');
            const qCnt = g.get('quantityOfContainers');
            const tempC = g.get('temperatureCelsius');
            const tempU = g.get('temperatureUnit');
            const vent = g.get('ventilation');
            const ventId = g.get('ventilationUnitId');
            if (isPallet) {
                qty?.setValidators([Validators.required, Validators.min(1)]);
                ct?.clearValidators();
                ctId?.clearValidators();
                qCnt?.clearValidators();
                tempC?.clearValidators();
                tempU?.clearValidators();
                vent?.clearValidators();
                ventId?.clearValidators();
            } else {
                qty?.clearValidators();
                ct?.clearValidators();
                ctId?.setValidators([Validators.required]);
                qCnt?.setValidators([Validators.required, Validators.min(1)]);
                tempC?.setValidators([Validators.required]);
                tempU?.setValidators([Validators.required]);
                vent?.setValidators([Validators.required]);
                ventId?.setValidators([Validators.required]);
            }
            qty?.updateValueAndValidity({ emitEvent: false });
            ct?.updateValueAndValidity({ emitEvent: false });
            ctId?.updateValueAndValidity({ emitEvent: false });
            qCnt?.updateValueAndValidity({ emitEvent: false });
            tempC?.updateValueAndValidity({ emitEvent: false });
            tempU?.updateValueAndValidity({ emitEvent: false });
            vent?.updateValueAndValidity({ emitEvent: false });
            ventId?.updateValueAndValidity({ emitEvent: false });
        }
    }

    private applyAllItemValidators(): void {
        this.instructivoForms.forEach((_, idx) => this.applyItemValidatorsFor(idx));
    }

    get items(): FormArray {
        return this.form.get('items') as FormArray;
    }

    get typeOfShipmentControl(): FormControl {
        return this.form.get('typeOfShipmentId') as FormControl;
    }

    get typeOfShipment(): TypeOfShipment {
        const raw = this.form?.getRawValue() as {
            typeOfShipmentId?: TypeOfShipment;
        };
        if (raw?.typeOfShipmentId != null) {
            return raw.typeOfShipmentId;
        }
        return (
            this.form.get('typeOfShipmentId')?.value || TypeOfShipment.Container
        );
    }

    private buildItemGroup(
        data?: Partial<CreateShippingInstructionItemDto>
    ): FormGroup {
        return this.fb.group({
            itemNumber: [data?.itemNumber ?? 1],
            // FK a `vwBookingContainers.Id` — persiste en `tblShippingInstructionItems.BookingContainerId`
            // para que `sp_InstructivoEmbarque_ApplyBooking` sepa qué contenedores ya están usados
            // (control de disponibilidad, "instructivo de embarque múltiple"). Sin este control el
            // valor nunca llega al payload de `create()` y el contenedor queda "pendiente" para
            // siempre, sin importar cuántos instructivos se creen (bug real: booking 070ISA1237108).
            bookingContainerId: [data?.bookingContainerId ?? null],
            quantityOfContainers: [data?.quantityOfContainers ?? null],
            quantityOfPallets: [data?.quantityOfPallets ?? null],
            // v1: NO se ingresan ContainerNumber/Seal/Marks (van en módulo Couriers v2)
            containerTypeId: [data?.containerTypeId ?? null],
            containerType: [data?.containerType ?? ''],
            palletTypeId: [data?.palletTypeId ?? null],
            palletType: [data?.palletType ?? ''],
            cargoType: [data?.cargoType ?? ''],
            coldTreatmentIndicatorId: [data?.coldTreatmentIndicatorId ?? null],
            coldTreatment: [data?.coldTreatment ?? ''],
            controlledAtmosphereId: [data?.controlledAtmosphereId ?? null],
            atmosphere: [data?.atmosphere ?? ''],
            temperatureCelsius: [data?.temperatureCelsius ?? null],
            temperatureUnit: [data?.temperatureUnit ?? 'C'],
            ventilation: [data?.ventilation ?? null],
            ventilationUnitId: [data?.ventilationUnitId ?? null],
            ventilationUnit: [data?.ventilationUnit ?? ''],
            atmosphereParams: [data?.atmosphereParams ?? ''],
            species: this.fb.array(
                data?.species && data.species.length > 0
                    ? data.species.map((s) => this.buildSpeciesGroup(s))
                    : []
            ),
        });
    }

    private buildSpeciesGroup(
        data?: Partial<CreateShippingInstructionSpeciesDto>
    ): FormGroup {
        return this.fb.group({
            speciesId: [data?.speciesId ?? null, Validators.required],
            speciesName: [data?.speciesName ?? '', Validators.required],
            varietyId: [data?.varietyId ?? null],
            variety: [data?.variety ?? ''],
            conditionId: [data?.conditionId ?? null, Validators.required],
            condition: [data?.condition ?? '', Validators.required],
            boxCount: [
                data?.boxCount ?? 0,
                [Validators.required, Validators.min(1)],
            ],
            netWeightKgPerBox: [
                data?.netWeightKgPerBox ?? null,
                [Validators.required, greaterThanZeroKg()],
            ],
            grossWeightKgPerBox: [
                data?.grossWeightKgPerBox ?? null,
                [Validators.required, greaterThanZeroKg()],
            ],
            typeOfPackageId: [
                data?.typeOfPackageId ?? null,
                Validators.required,
            ],
            packageType: [data?.packageType ?? ''],
            brand: [data?.brand ?? ''],
            fobUnitPrice: [
                data?.fobUnitPrice ?? null,
                [Validators.required, Validators.min(0)],
            ],
        });
    }

    private addItemFromData(panelIdx: number, item: CreateShippingInstructionItemDto): void {
        this.getItemsFor(panelIdx).push(this.buildItemGroup(item));
    }

    addItem(panelIdx: number, defaults?: Partial<CreateShippingInstructionItemDto>): void {
        const items = this.getItemsFor(panelIdx);
        items.push(this.buildItemGroup(defaults));
        this.applyItemValidatorsFor(panelIdx);
        this.instructivoForms[panelIdx].updateValueAndValidity({ emitEvent: false });
    }

    removeItem(panelIdx: number, itemIdx: number): void {
        const items = this.getItemsFor(panelIdx);
        if (items?.length > 0) {
            items.removeAt(itemIdx);
            this.applyItemValidatorsFor(panelIdx);
            this.instructivoForms[panelIdx].updateValueAndValidity({ emitEvent: false });
        }
    }

    getSpecies(panelIdx: number, itemIndex: number): FormArray {
        return this.getItemsFor(panelIdx)?.at(itemIndex)?.get('species') as FormArray;
    }

    addSpecies(panelIdx: number, itemIndex: number): void {
        this.getSpecies(panelIdx, itemIndex).push(this.buildSpeciesGroup());
        this.preloadSpeciesCatalogIfNeeded();
    }

    removeSpecies(panelIdx: number, itemIndex: number, speciesIndex: number): void {
        const species = this.getSpecies(panelIdx, itemIndex);
        if (species?.length > 0) {
            species.removeAt(speciesIndex);
        }
    }

    // Helpers for calculated fields
    getTotalNetWeightKg(panelIdx: number, itemIndex: number, speciesIndex: number): number {
        const species = this.getSpecies(panelIdx, itemIndex).at(speciesIndex);
        const boxCount = species.get('boxCount')?.value || 0;
        const netPerBox = species.get('netWeightKgPerBox')?.value || 0;
        return boxCount * netPerBox;
    }

    getTotalGrossWeightKg(panelIdx: number, itemIndex: number, speciesIndex: number): number {
        const species = this.getSpecies(panelIdx, itemIndex).at(speciesIndex);
        const boxCount = species.get('boxCount')?.value || 0;
        const grossPerBox = species.get('grossWeightKgPerBox')?.value || 0;
        return boxCount * grossPerBox;
    }

    getFobItemTotal(panelIdx: number, itemIndex: number, speciesIndex: number): number {
        const species = this.getSpecies(panelIdx, itemIndex).at(speciesIndex);
        const boxCount = species.get('boxCount')?.value || 0;
        const unitPrice = species.get('fobUnitPrice')?.value || 0;
        return boxCount * unitPrice;
    }

    onSpeciesFocus(): void {
        this.preloadSpeciesCatalogIfNeeded();
    }

    onConditionFocus(): void {
        this.preloadConditionCatalogIfNeeded();
    }

    onSpeciesSelected(panelIdx: number, itemIndex: number, speciesIndex: number, speciesId: number | null): void {
        const group = this.getSpecies(panelIdx, itemIndex).at(speciesIndex) as FormGroup;
        const key = this.speciesKey(panelIdx, itemIndex, speciesIndex);
        if (!speciesId) {
            group.patchValue({ speciesName: '', varietyId: null, variety: '' }, { emitEvent: false });
            this.varietiesBySpeciesKey.set(key, []);
            return;
        }
        const selected = this.speciesCatalog.find((s) => s.speciesId === speciesId);
        group.patchValue({ speciesName: selected?.speciesName ?? '', varietyId: null, variety: '' }, { emitEvent: false });
        this.service.getVarietiesCatalog(speciesId).subscribe({
            next: (rows) => this.varietiesBySpeciesKey.set(key, rows),
            error: () => {
                this.varietiesBySpeciesKey.set(key, []);
                console.error('Error cargando catálogo de variedades');
            },
        });
    }

    isVarietyDisabled(panelIdx: number, itemIndex: number, speciesIndex: number): boolean {
        const group = this.getSpecies(panelIdx, itemIndex).at(speciesIndex) as FormGroup;
        const speciesId = group.get('speciesId')?.value;
        return speciesId == null;
    }

    onVarietySelected(panelIdx: number, itemIndex: number, speciesIndex: number, varietyId: number | null): void {
        const group = this.getSpecies(panelIdx, itemIndex).at(speciesIndex) as FormGroup;
        const options = this.getVarietiesFor(panelIdx, itemIndex, speciesIndex);
        const selected = options.find((v) => v.varietyId === varietyId);
        group.patchValue({ variety: selected?.varietyName ?? '' }, { emitEvent: false });
    }

    onConditionSelected(panelIdx: number, itemIndex: number, speciesIndex: number, conditionId: number | null): void {
        const group = this.getSpecies(panelIdx, itemIndex).at(speciesIndex) as FormGroup;
        const selected = this.conditionCatalog.find((v) => v.conditionId === conditionId);
        group.patchValue({ condition: selected?.conditionName ?? '' }, { emitEvent: false });
    }

    getVarietiesFor(panelIdx: number, itemIndex: number, speciesIndex: number): VarietyCatalogItemDto[] {
        return this.varietiesBySpeciesKey.get(this.speciesKey(panelIdx, itemIndex, speciesIndex)) ?? [];
    }

    isContainerShipment(): boolean {
        return this.typeOfShipment === TypeOfShipment.Container;
    }

    isPalletShipment(): boolean {
        return this.typeOfShipment === TypeOfShipment.Pallet;
    }

    /** True si todos los paneles de ítems son válidos (valida species requeridas). */
    isInstructivoFormsValid(): boolean {
        if (this.instructivoForms.length === 0) return false;
        return this.instructivoForms.every((f) => f.valid);
    }

    markAllInstructivoFormsTouched(): void {
        this.instructivoForms.forEach((f) => f.markAllAsTouched());
    }

    get cargoItemsCardTitle(): string {
        return this.typeOfShipment === TypeOfShipment.Pallet
            ? 'Items de Carga (Pallet)'
            : 'Items de Carga (Contenedores)';
    }
}
